import { initComponent } from './core/init-component';

const EASING = 'cubic-bezier(0.42, 0, 0.58, 1)';
const DURATION = '0.4s';
const ANIMATION_MS = parseFloat(DURATION) * 1000;

initComponent('.js-ls-shop-the-look', (el) => {
  const bgLayers = el.querySelectorAll<HTMLElement>('.js-ls-stl-bg');
  const lookLayers = el.querySelectorAll<HTMLElement>('.js-ls-stl-look');
  const slides = el.querySelectorAll<HTMLElement>('.js-ls-stl-slide');
  const totalSlides = slides.length;
  const allImageLayers = [...bgLayers, ...lookLayers];
  let currentIndex = 0;
  let isAnimating = false;
  let animatingTimer: ReturnType<typeof setTimeout>;
  let cachedRect = el.getBoundingClientRect();
  let resizeTimer: ReturnType<typeof setTimeout>;

  function updateBoundary() {
    el.dataset.slide = String(currentIndex);
    el.toggleAttribute('data-at-start', currentIndex === 0);
    el.toggleAttribute('data-at-end', currentIndex === totalSlides - 1);
    el.removeAttribute('data-hover-prev');
    el.removeAttribute('data-hover-next');
  }

  function init() {
    [...bgLayers, ...lookLayers].forEach(layer => {
      layer.style.transform = Number(layer.dataset.index) === 0 ? 'translateX(0)' : 'translateX(100%)';
    });
    slides.forEach((slide, i) => {
      if (i !== 0) slide.style.display = 'none';
    });
    updateBoundary();
  }

  function goTo(newIndex: number) {
    if (newIndex < 0 || newIndex >= totalSlides || newIndex === currentIndex || isAnimating) return;
    isAnimating = true;
    clearTimeout(animatingTimer);
    animatingTimer = setTimeout(() => { isAnimating = false; }, ANIMATION_MS);

    const direction = newIndex > currentIndex ? 1 : -1;
    const fromIndex = currentIndex;

    // Position incoming image layers off-screen without transition
    allImageLayers.forEach(layer => {
      if (Number(layer.dataset.index) === newIndex) {
        layer.style.transition = 'none';
        layer.style.transform = `translateX(${direction * 100}%)`;
      }
    });

    // Switch slide content (hotspots, nav zones) instantly
    slides.forEach((slide, i) => {
      slide.style.display = i === newIndex ? '' : 'none';
    });

    currentIndex = newIndex;
    updateBoundary();
    resetCursorFollow();

    // Force reflow so transition fires correctly
    el.getBoundingClientRect();

    // Animate image layers
    allImageLayers.forEach(layer => {
      const i = Number(layer.dataset.index);
      layer.style.transition = `transform ${DURATION} ${EASING}`;
      if (i === fromIndex) {
        layer.style.transform = `translateX(${-direction * 100}%)`;
      } else if (i === newIndex) {
        layer.style.transform = 'translateX(0)';
      }
    });
  }

  function selectBlock(blockEl: HTMLElement) {
    const index = Number(blockEl.dataset.index);
    if (!isNaN(index)) goTo(index);
  }

  init();

  if (totalSlides <= 1) return { selectBlock };

  const mdPrevSpans = Array.from(slides).map(s => s.querySelector<HTMLElement>('.js-ls-stl-card span.js-ls-stl-prev'));
  const mdNextSpans = Array.from(slides).map(s => s.querySelector<HTMLElement>('.js-ls-stl-card span.js-ls-stl-next'));
  const cards = Array.from(slides).map(s => s.querySelector<HTMLElement>('.js-ls-stl-card'));
  const isMd = window.matchMedia('(min-width: 1024px)');
  let rafId = 0;

  function updateCursorFollow(clientX: number, clientY: number) {
    if (!isMd.matches) return;
    const card = cards[currentIndex];
    const prev = mdPrevSpans[currentIndex];
    const next = mdNextSpans[currentIndex];
    if (!card || (!prev && !next)) return;
    const rect = card.getBoundingClientRect();
    const yOffset = clientY - (rect.top + rect.height / 2);
    if (prev) prev.style.transform = `translateY(calc(-50% + ${yOffset}px)) translateX(${clientX - rect.left}px)`;
    if (next) next.style.transform = `translateY(calc(-50% + ${yOffset}px)) translateX(${clientX - rect.right}px)`;
  }

  function resetCursorFollow() {
    cancelAnimationFrame(rafId);
    mdPrevSpans.forEach(s => { if (s) s.style.transform = ''; });
    mdNextSpans.forEach(s => { if (s) s.style.transform = ''; });
  }

  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      cachedRect = el.getBoundingClientRect();
      if (!isMd.matches) resetCursorFollow();
    }, 150);
  }
  window.addEventListener('resize', onResize);

  function onClick(evt: MouseEvent) {
    const target = evt.target as HTMLElement;
    if (target.closest('.js-ls-stl-next')) goTo(currentIndex + 1);
    else if (target.closest('.js-ls-stl-prev')) goTo(currentIndex - 1);
  }

  function onMouseMove(evt: MouseEvent) {
    const target = evt.target as HTMLElement;
    if (target.closest('.js-ls-stl-card') && !target.closest('.js-ls-stl-prev') && !target.closest('.js-ls-stl-next')) {
      el.removeAttribute('data-hover-prev');
      el.removeAttribute('data-hover-next');
      return;
    }
    const x = evt.clientX - cachedRect.left;
    if (x < cachedRect.width / 2) {
      if (!el.hasAttribute('data-at-start')) el.setAttribute('data-hover-prev', '');
      el.removeAttribute('data-hover-next');
    } else {
      if (!el.hasAttribute('data-at-end')) el.setAttribute('data-hover-next', '');
      el.removeAttribute('data-hover-prev');
    }
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => updateCursorFollow(evt.clientX, evt.clientY));
  }

  function onMouseLeave() {
    el.removeAttribute('data-hover-prev');
    el.removeAttribute('data-hover-next');
    cancelAnimationFrame(rafId);
    setTimeout(() => {
      mdPrevSpans.forEach(s => { if (s) s.style.transform = ''; });
      mdNextSpans.forEach(s => { if (s) s.style.transform = ''; });
    }, 200);
  }

  el.addEventListener('click', onClick);
  el.addEventListener('mousemove', onMouseMove);
  el.addEventListener('mouseleave', onMouseLeave);

  return {
    unload() {
      el.removeEventListener('click', onClick);
      el.removeEventListener('mousemove', onMouseMove);
      el.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('resize', onResize);
      clearTimeout(resizeTimer);
      clearTimeout(animatingTimer);
      resetCursorFollow();
    },
    selectBlock,
  };
});
